import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { imageSrc } from "@/lib/images";
import {
  Check, X, Inbox, Copy, RefreshCw, Link2, ChevronDown, ChevronUp, Trash2,
} from "lucide-react";

const SECTION_LABEL = { cast: "Cast", crew: "Crew", company: "Company" };

const Row = ({ label, value }) => {
  if (value === null || value === undefined || value === "" ||
      (Array.isArray(value) && value.length === 0)) return null;
  const text = Array.isArray(value) ? value.join(", ") : String(value);
  return (
    <div className="grid grid-cols-[150px_1fr] gap-3 py-1 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="break-words whitespace-pre-line">{text}</span>
    </div>
  );
};

export function ShareIntakeLink() {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/intake-link").then(({ data }) => setInfo(data))
      .catch((err) => console.error("Could not load the sign-up link", err));
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const url = info ? `${window.location.origin}${info.path}` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — paste it anywhere");
    } catch { toast.error("Copy failed — select the link and copy it manually"); }
  };

  const rotate = async () => {
    if (!window.confirm("Create a new link? The old one stops working immediately.")) return;
    const { data } = await api.post("/intake-link/rotate");
    setInfo((prev) => ({ ...prev, ...data }));
    toast.success("New link created");
  };

  const setEnabled = async (value) => {
    await api.put("/intake-link", { intake_enabled: value });
    setInfo((prev) => ({ ...prev, enabled: value }));
    toast.success(value ? "Sign-up link is live" : "Sign-up link switched off");
  };

  const saveCopy = async () => {
    await api.put("/intake-link", {
      intake_headline: info.headline || null, intake_intro: info.intro || null,
    });
    toast.success("Wording saved");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="share-intake-link-btn">
          <Link2 className="w-4 h-4 mr-2" /> Sign-up link
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl" data-testid="intake-link-dialog">
        <DialogHeader><DialogTitle>Public sign-up link</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">
          Share this with cast, crew and companies. Anything they submit waits in Requests for your
          approval before it becomes a contact.
        </p>
        {info && (
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input readOnly value={url} data-testid="intake-link-url" />
              <Button variant="outline" data-testid="intake-link-copy" onClick={copy}>
                <Copy className="w-4 h-4" />
              </Button>
              <Button variant="outline" data-testid="intake-link-rotate" onClick={rotate}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center gap-3 rounded-lg border border-border p-3">
              <div className="text-sm">
                <div className="font-medium">Link is {info.enabled ? "live" : "switched off"}</div>
                <div className="text-xs text-muted-foreground">
                  Turn it off and the page shows a polite "not active" message.
                </div>
              </div>
              <Switch className="ml-auto" data-testid="intake-link-toggle"
                      checked={!!info.enabled} onCheckedChange={setEnabled} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Headline on the form</Label>
              <Input data-testid="intake-headline" value={info.headline || ""}
                     placeholder="Join the cast & crew hub"
                     onChange={(e) => setInfo({ ...info, headline: e.target.value })} />
              <Label className="text-xs">Intro paragraph</Label>
              <Input data-testid="intake-intro" value={info.intro || ""}
                     placeholder="Tell us who you are and what you do…"
                     onChange={(e) => setInfo({ ...info, intro: e.target.value })} />
              <Button size="sm" data-testid="intake-copy-save" onClick={saveCopy}>Save wording</Button>
            </div>
            <a href={url} target="_blank" rel="noreferrer" data-testid="intake-link-preview"
               className="inline-block text-xs text-[hsl(var(--studio-primary))] hover:underline">
              Open the form as they'd see it →
            </a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SubmissionCard({ submission, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const p = submission.payload || {};

  const act = async (what) => {
    setBusy(true);
    try {
      if (what === "delete") await api.delete(`/contact-submissions/${submission.id}`);
      else await api.post(`/contact-submissions/${submission.id}/${what}`, {});
      toast.success(what === "approve" ? `${p.name} added to Contacts`
        : what === "reject" ? "Request declined" : "Request removed");
      await onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not do that");
    } finally { setBusy(false); }
  };

  return (
    <Card className="studio-card p-5" data-testid={`submission-${submission.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-medium truncate">{p.name}</div>
          <div className="text-xs text-muted-foreground truncate">
            {[p.primary_role, p.email, p.phone, p.location].filter(Boolean).join(" · ")}
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            {(submission.sections || []).map((s) => (
              <Badge key={s} variant="secondary" className="text-[10px] font-normal">
                {SECTION_LABEL[s] || s}
              </Badge>
            ))}
            {p.company?.name && (
              <Badge variant="outline" className="text-[10px] font-normal">{p.company.name}</Badge>
            )}
            {submission.status !== "pending" && (
              <Badge variant={submission.status === "approved" ? "default" : "destructive"}
                     className="text-[10px] font-normal">{submission.status}</Badge>
            )}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-1">
          {submission.status === "pending" ? (
            <>
              <Button size="sm" disabled={busy} data-testid={`submission-approve-${submission.id}`}
                      onClick={() => act("approve")}>
                <Check className="w-3.5 h-3.5 mr-1" /> Approve
              </Button>
              <Button size="sm" variant="ghost" disabled={busy}
                      data-testid={`submission-reject-${submission.id}`} onClick={() => act("reject")}>
                <X className="w-3.5 h-3.5 mr-1" /> Decline
              </Button>
            </>
          ) : (
            <Button size="sm" variant="ghost" disabled={busy}
                    data-testid={`submission-delete-${submission.id}`} onClick={() => act("delete")}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
          <Button size="sm" variant="ghost" data-testid={`submission-expand-${submission.id}`}
                  onClick={() => setOpen((o) => !o)}>
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {open && (
        <div className="mt-3 pt-3 border-t border-border" data-testid={`submission-detail-${submission.id}`}>
          {(p.photo_url || p.resume_url) && (
            <div className="mb-3 flex items-center gap-3">
              {p.photo_url && (
                <img src={imageSrc(p.photo_url)} alt="Headshot"
                     data-testid={`submission-photo-${submission.id}`}
                     className="h-16 w-16 rounded-full border border-border object-cover" />
              )}
              {p.resume_url && (
                <a href={imageSrc(p.resume_url)} target="_blank" rel="noreferrer"
                   data-testid={`submission-resume-${submission.id}`}
                   className="text-xs underline text-[hsl(var(--studio-primary))]">
                  Open resume / CV
                </a>
              )}
            </div>
          )}
          <Row label="Goes by" value={p.preferred_name} />
          <Row label="Pronouns" value={p.pronouns} />
          <Row label="Other roles" value={p.secondary_roles} />
          <Row label="Skills" value={p.skills} />
          <Row label="Languages" value={p.languages} />
          <Row label="Height" value={p.height_cm} />
          <Row label="Hair / eyes" value={[p.hair_colour, p.eye_colour].filter(Boolean).join(" / ")} />
          <Row label="Ethnicity" value={p.ethnicity} />
          <Row label="Measurements" value={p.measurements} />
          <Row label="Unions" value={p.unions} />
          <Row label="Agent" value={[p.agent_name, p.agent_company, p.agent_email, p.agent_phone].filter(Boolean).join(" · ")} />
          <Row label="Rates" value={[p.day_rate && `day ${p.day_rate}`, p.half_day_rate && `half ${p.half_day_rate}`, p.weekly_rate && `week ${p.weekly_rate}`].filter(Boolean).join(" · ")} />
          <Row label="Availability" value={p.availability_notes} />
          <Row label="Travel" value={p.travel_willingness} />
          <Row label="Equipment" value={p.equipment_owned} />
          <Row label="Has transport" value={p.has_own_transport ? "yes" : null} />
          <Row label="Has equipment" value={p.has_own_equipment ? "yes" : null} />
          <Row label="Public liability" value={p.has_public_liability ? "yes" : null} />
          <Row label="Credits" value={p.credits_text} />
          <Row label="Showreel" value={p.showreel_url} />
          <Row label="Website" value={p.website} />
          <Row label="Socials" value={Object.entries(p.socials || {}).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`)} />
          <Row label="Notes" value={p.notes} />
          {p.company?.name && (
            <>
              <Row label="Company" value={p.company.name} />
              <Row label="Company type" value={p.company.type} />
              <Row label="Company site" value={p.company.website} />
              <Row label="Services" value={p.company.services} />
              <Row label="Team size" value={p.company.team_size} />
              <Row label="Established" value={p.company.established_year} />
            </>
          )}
        </div>
      )}
    </Card>
  );
}

export function SubmissionsPanel({ onChanged }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/contact-submissions", { params: { status } })
      .then(({ data }) => setRows(data))
      .catch((err) => console.error("Could not load sign-up requests", err))
      .finally(() => setLoading(false));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const afterChange = async () => { load(); await onChanged(); };

  return (
    <div className="space-y-4" data-testid="submissions-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm text-muted-foreground">
          People who filled in your public sign-up link. Approve and they become contacts, tagged
          and filed into the right lists.
        </p>
        <div className="ml-auto flex gap-1">
          {["pending", "approved", "rejected", "all"].map((s) => (
            <Button key={s} size="sm" variant={status === s ? "default" : "ghost"}
                    data-testid={`submissions-filter-${s}`} onClick={() => setStatus(s)}>
              {s}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading requests…</div>
      ) : rows.length === 0 ? (
        <Card className="studio-card p-10 text-center" data-testid="submissions-empty">
          <Inbox className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" strokeWidth={1.5} />
          <div className="text-lg font-medium">Nothing waiting</div>
          <p className="text-sm text-muted-foreground mt-1">
            Share your sign-up link and new cast, crew and companies will land here.
          </p>
        </Card>
      ) : (
        rows.map((r) => <SubmissionCard key={r.id} submission={r} onChanged={afterChange} />)
      )}
    </div>
  );
}
